/**
 * Code-owned realtime policy constants (Phase 1).
 *
 * Everything a client needs to *decide* about the connection — how long to
 * remember event ids, how to back off a failed bootstrap, how often to poll
 * when the socket is down — lives here rather than being sprinkled across the
 * service. `src/config/notifications.ts` re-exports the UX-facing subset so
 * there is exactly one source for each number.
 */

/**
 * How many `event_id`s to remember for de-duplication. Phase 1 fixed this at
 * 500; Phase 6 raises it to 2,500 for parity with the ERPat web client
 * (DEC-074): one catch-up drain can replay up to 2,000 events (20 pages × 100),
 * and a window smaller than a drain lets a duplicate inside it re-apply.
 */
export const DEDUPE_WINDOW_SIZE = 2_500;

/**
 * Seconds between inbox/badge polls while the realtime socket is NOT
 * connected. Mirrors the server's `poll_interval_seconds` default; the
 * bootstrap value overrides it per tenant when present.
 */
export const POLL_FALLBACK_SECONDS = 60;

/**
 * Bootstrap retry back-off (the one reconnect loop pusher-js does not own —
 * it cannot start until `/me/realtime/bootstrap` has answered). Exponential
 * with full jitter, capped, so a backend outage does not produce a thundering
 * herd of identical retries from every open tab.
 */
export const BOOTSTRAP_RETRY_BASE_MS = 5_000;
export const BOOTSTRAP_RETRY_MAX_MS = 60_000;
export const BOOTSTRAP_RETRY_MULTIPLIER = 2;

/**
 * Pusher protocol heartbeat defaults. `activityTimeout` is what the gateway
 * advertises in `pusher:connection_established` (bootstrap carries the same
 * number); `pongTimeout` is how long we wait for `pusher:pong` before pusher-js
 * declares the socket dead and reconnects.
 */
export const ACTIVITY_TIMEOUT_SECONDS_DEFAULT = 120;
export const PONG_TIMEOUT_MS = 30_000;

/**
 * pusher-js waits this long in `connecting` before it reports `unavailable`
 * (its default is 10 s). Shorter here so the settings page flips to
 * "Reconnecting…" promptly on flaky mobile networks.
 */
export const UNAVAILABLE_TIMEOUT_MS = 8_000;

/* ── Phase 4/5: cursor catch-up, close codes, receipts ─────────────────────── */

/** Page size for `GET /me/notifications/changes` (the server caps at 100). */
export const CHANGES_PAGE_SIZE = 100;

/**
 * Most pages one drain walks before it stops and re-bootstraps instead. A
 * client that is 2,000 events behind is better served by a fresh snapshot
 * than by replaying every one of them.
 */
export const DRAIN_MAX_PAGES = 20;

/**
 * Idle catch-up cadence while the socket is CONNECTED — a safety net for a
 * frame the gateway dropped without closing the socket. Cheap (one request
 * that usually answers `has_more: false` with zero events).
 */
export const IDLE_DRAIN_INTERVAL_MS = 10 * 60_000;

/** Drain retry back-off on a network error / 5xx: 2 s doubling to 60 s, six tries. */
export const DRAIN_RETRY_BASE_MS = 2_000;
export const DRAIN_RETRY_MAX_MS = 60_000;
export const DRAIN_RETRY_ATTEMPTS = 6;

/**
 * Gateway close codes (Pusher protocol 7 + the gateway's 41xx/42xx range).
 * pusher-js treats 4000–4099 as "refused": it calls `disconnect()` and never
 * retries on its own, so the client must decide what to do next.
 *   4001 app not found · 4007 unsupported protocol · 4009 connection revoked
 *   (logout, password/role change, tenant switch) → re-bootstrap after jitter;
 *   4100 over capacity → pusher-js backs off, we poll meanwhile;
 *   4200 re-authentication (every ~30 min, expected) / 4201 → pusher-js
 *   reconnects immediately; not an error.
 */
export const CLOSE_CODES_REFUSED: readonly number[] = [4001, 4007, 4009];
export const CLOSE_CODE_OVER_CAPACITY = 4100;
export const CLOSE_CODE_REAUTH = 4200;
export const CLOSE_CODE_RETRY_NOW = 4201;

/** Jitter before re-bootstrapping after a refused close (so a fleet does not stampede). */
export const REFUSED_REBOOTSTRAP_MIN_MS = 2_000;
export const REFUSED_REBOOTSTRAP_MAX_MS = 5_000;

/* ── Phase 6: refusal cadences, reconcile, outbox, pending actions ─────────── */

/**
 * A refused close code OTHER than 4009 (4001, 4007, and any other 4001–4099
 * the gateway may add) re-bootstraps at most once per this interval, on that
 * cadence — never once and then abandoned. 4009 keeps the 2–5 s jitter above.
 * Mirrors the ERPat web client (DEC-079).
 */
export const REFUSED_REBOOTSTRAP_MIN_INTERVAL_MS = 60_000;

/**
 * A `changes` page the server REFUSES (403 / 404 / 410 / 422) re-bootstraps at
 * most once per this interval; a second refusal inside the window leaves the
 * sync state `degraded` and lets the idle drain, a reconnect or a reconcile
 * retry instead of looping bootstrap → drain → 4xx. Mirrors DEC-080.
 */
export const DRAIN_REFUSAL_REBOOTSTRAP_MIN_INTERVAL_MS = 60_000;

/**
 * One REST reconcile (badge + conversation list refresh, never a render) per
 * this interval, however many rejected / unknown / mismatched frames arrive.
 */
export const RECONCILE_THROTTLE_MS = 5_000;

/**
 * Most queued outbox entries per thread scope. At the bound an enqueue is
 * REFUSED (`OutboxFullError`, toasted) — nothing already queued is ever dropped.
 */
export const OUTBOX_MAX_ENTRIES_PER_SCOPE = 20;

/**
 * The outbox flush the realtime service triggers on `connected` and on the
 * reachability edge runs at most once per this interval; a thread open flushes
 * its own scope un-throttled.
 */
export const OUTBOX_FLUSH_MIN_INTERVAL_MS = 10_000;

/**
 * `opened` receipts recorded before the bootstrap has answered (a cold-start
 * tap, a deep link) are parked in a bounded queue and posted once the binding
 * says receipts are enabled; beyond this many the oldest is dropped.
 */
export const RECORD_OPENED_QUEUE_MAX = 20;

/**
 * A realtime target parked before sign-in (a device-alert tap, a deep link) is
 * replayed only if it is younger than this when the session becomes
 * authenticated for the same tenant; older ones are dropped silently.
 */
export const PENDING_ACTION_TTL_MS = 10 * 60_000;

/** Receipts are batched: flush every 5 s or at 100 entries, whichever comes first. */
export const RECEIPT_FLUSH_INTERVAL_MS = 5_000;
export const RECEIPT_BATCH_MAX = 100;

/**
 * Trailing coalesce window per envelope type. A burst of ten `notification.*`
 * frames (a bulk approval, a drain) triggers ONE badge refresh and ONE repaint
 * tick, not ten.
 */
export const DISPATCH_COALESCE_MS = 400;

/** `localStorage` key prefix of the persisted cursor: `<prefix>:<tenant>:<user>`. */
export const CURSOR_STORAGE_PREFIX = 'syntry.realtime_cursor';

/** Compute the wait before bootstrap retry number `attempt` (1-based), with full jitter. */
export function bootstrapRetryDelayMs(attempt: number): number {
  const exponent = Math.max(0, attempt - 1);
  const ceiling = Math.min(
    BOOTSTRAP_RETRY_MAX_MS,
    BOOTSTRAP_RETRY_BASE_MS * BOOTSTRAP_RETRY_MULTIPLIER ** exponent,
  );
  // Full jitter: uniform in [base, ceiling] so retries never align across tabs
  // while still guaranteeing at least the base pause.
  const floor = Math.min(BOOTSTRAP_RETRY_BASE_MS, ceiling);
  return Math.floor(floor + Math.random() * (ceiling - floor));
}

/** Wait before drain retry number `attempt` (1-based): 2 s, 4 s, 8 s … capped at 60 s, jittered. */
export function drainRetryDelayMs(attempt: number): number {
  const exponent = Math.max(0, attempt - 1);
  const ceiling = Math.min(DRAIN_RETRY_MAX_MS, DRAIN_RETRY_BASE_MS * 2 ** exponent);
  const floor = Math.min(DRAIN_RETRY_BASE_MS, ceiling);
  return Math.floor(floor + Math.random() * (ceiling - floor));
}

/** Uniform jitter in [2 s, 5 s] before re-bootstrapping after a refused close. */
export function refusedRebootstrapDelayMs(): number {
  return Math.floor(
    REFUSED_REBOOTSTRAP_MIN_MS + Math.random() * (REFUSED_REBOOTSTRAP_MAX_MS - REFUSED_REBOOTSTRAP_MIN_MS),
  );
}
